import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { getTextColorStyle } from "@components/FilePreview/FilePreview.styles.tsx";
import type { FilePreviewProps } from "@components/FilePreview/FilePreview.types";
import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useQuery } from "@tanstack/react-query";
import { renderAsync } from "docx-preview";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import * as XLSX from "xlsx";

const fallbackWorkerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

if (pdfjs.GlobalWorkerOptions.workerSrc !== fallbackWorkerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = fallbackWorkerSrc;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  testId,
  title,
  imageUrl,
  onPreview,
  enablePreviewZoom = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [responseImgURL, setResponseImgURL] = useState<string | undefined>(undefined);
  const [fileExtension, setFileExtension] = useState<string | undefined>(undefined);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfScale, setPdfScale] = useState<number>(1);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [pdfViewportWidth, setPdfViewportWidth] = useState<number>(0);
  const [isPdfDragging, setIsPdfDragging] = useState<boolean>(false);
  const { data: document, isLoading } = useQuery({
    queryKey: ["document", imageUrl],
    queryFn: () => getDocumentsWithToken(imageUrl, SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE),
  });
  const docxRef = useRef<HTMLDivElement>(null);
  const excelRef = useRef<HTMLDivElement>(null);
  const pdfViewportRef = useRef<HTMLDivElement>(null);
  const dragStartXRef = useRef<number>(0);
  const dragStartYRef = useRef<number>(0);
  const dragStartScrollLeftRef = useRef<number>(0);
  const dragStartScrollTopRef = useRef<number>(0);

  const isImage = !!fileExtension && /^(jpg|jpeg|png|gif|bmp)$/i.test(fileExtension);
  const isPdf = fileExtension === "pdf";
  const isDocx = fileExtension === "doc" || fileExtension === "docx";
  const isExcel = fileExtension === "xls" || fileExtension === "xlsx";
  const previewZoomStep = 0.1;

  useEffect(() => {
    if (imageUrl) {
      // Extract file extension from imageUrl
      const match = imageUrl.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
      if (match) {
        setFileExtension(match[1].toLowerCase());
      } else {
        setFileExtension(undefined);
      }
    }
  }, [imageUrl]);

  // Create object URL only for image/pdf to avoid unnecessary blob URL generation
  useEffect(() => {
    if (!document || !(document instanceof Blob)) return;
    if (!(isImage || isPdf)) return; // not needed for docx/xlsx custom renderers
    const imgURL = URL.createObjectURL(document);
    setResponseImgURL(imgURL);
    return () => URL.revokeObjectURL(imgURL); // cleanup
  }, [document, isImage, isPdf]);

  // Render DOCX preview when applicable
  useEffect(() => {
    if (!document || !(document instanceof Blob) || !isDocx || !docxRef.current) return;
    const container = docxRef.current;
    (async () => {
      try {
        await renderAsync(document, container, undefined, {
          className: "docx-wrapper",
          inWrapper: true,
        });
      } catch (e) {
        container.innerHTML = `<div class="p-2 text-red-600 text-xs">${t("security-clearance.preview-unsupported-type")}</div>`;
        console.error("DOCX render failed", e);
      }
    })();
    return () => {
      if (container) container.innerHTML = "";
    };
  }, [document, isDocx, t]);

  // Render Excel preview (first sheet -> HTML table)
  useEffect(() => {
    if (!document || !(document instanceof Blob) || !isExcel || !excelRef.current) return;
    const container = excelRef.current;
    (async () => {
      try {
        const arrayBuffer = await document.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) return;
        const worksheet = workbook.Sheets[firstSheetName];
        const html = XLSX.utils.sheet_to_html(worksheet, { id: "excel-preview-table" });
        // Basic sanitization: remove external links' target attributes if any
        container.innerHTML = html;
        // Optional: shrink font / make scrollable
        const tableEl = container.querySelector("table");
        if (tableEl) {
          tableEl.classList.add("min-w-full", "text-[10px]", "border", "border-gray-200");
        }
      } catch (e) {
        console.error("Excel render failed", e);
        container.innerHTML = `<div class="p-2 text-red-600 text-xs">${t("security-clearance.preview-unsupported-type")}</div>`;
      }
    })();
    return () => {
      if (container) container.innerHTML = "";
    };
  }, [document, isExcel, t]);

  useEffect(() => {
    if (!isPdf) {
      setPdfNumPages(0);
      setPdfScale(1);
      setPdfLoadError(null);
      setPdfViewportWidth(0);
      return;
    }

    setPdfScale(1);
    setPdfLoadError(null);
  }, [isPdf, responseImgURL]);

  useEffect(() => {
    if (!isPdf || !pdfViewportRef.current) return;

    const updateWidth = () => {
      if (!pdfViewportRef.current) return;
      setPdfViewportWidth(pdfViewportRef.current.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(pdfViewportRef.current);
    return () => observer.disconnect();
  }, [isPdf, responseImgURL]);

  const handlePdfPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!enablePreviewZoom || !pdfViewportRef.current || pdfScale <= 1) return;

    dragStartXRef.current = event.clientX;
    dragStartYRef.current = event.clientY;
    dragStartScrollLeftRef.current = pdfViewportRef.current.scrollLeft;
    dragStartScrollTopRef.current = pdfViewportRef.current.scrollTop;
    setIsPdfDragging(true);
    pdfViewportRef.current.setPointerCapture(event.pointerId);
  };

  const handlePdfPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPdfDragging || !pdfViewportRef.current) return;

    const deltaX = event.clientX - dragStartXRef.current;
    const deltaY = event.clientY - dragStartYRef.current;

    pdfViewportRef.current.scrollLeft = dragStartScrollLeftRef.current - deltaX;
    pdfViewportRef.current.scrollTop = dragStartScrollTopRef.current - deltaY;
  };

  const stopPdfDragging = () => {
    if (!isPdfDragging) return;
    setIsPdfDragging(false);
  };

  if (isLoading || !fileExtension) return null;

  // For non-image/pdf types we don't rely on responseImgURL;
  const needsBlobUrl = isImage || isPdf;
  const canRender = !needsBlobUrl || !!responseImgURL;

  if (!canRender) return null; // still preparing blob url

  return (
    <div data-testid={testId} className="w-fit max-w-full no-break">
      <a
        className="block text-sm font-bold mb-3 underline cursor-pointer no-print"
        style={getTextColorStyle(theme)}
        onClick={(e) => {
          e.preventDefault();
          if (onPreview && responseImgURL) {
            onPreview(responseImgURL);
          }
        }}
      >
        {title}
      </a>
      <div className="w-fit max-w-full">
        {isImage ? (
          enablePreviewZoom ? (
            <div className="w-full max-w-full">
              <TransformWrapper
                initialScale={1}
                minScale={1}
                maxScale={4}
                wheel={{ step: 0.01 }}
                doubleClick={{ mode: "zoomIn", step: previewZoomStep }}
              >
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 no-print">
                      <button
                        type="button"
                        className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                        onClick={() => zoomOut(previewZoomStep)}
                        aria-label="Zoom out"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                        onClick={() => zoomIn(previewZoomStep)}
                        aria-label="Zoom in"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                        onClick={() => resetTransform()}
                      >
                        Reset
                      </button>
                    </div>
                    <TransformComponent
                      wrapperClass="max-w-full h-auto overflow-hidden border border-gray-200 rounded"
                      contentClass="flex max-w-full items-center justify-center"
                    >
                      <img
                        src={responseImgURL}
                        alt={title}
                        className="h-auto md:h-[450px] w-auto max-w-full object-contain rounded cursor-grab active:cursor-grabbing"
                      />
                    </TransformComponent>
                  </div>
                )}
              </TransformWrapper>
            </div>
          ) : (
            <img
              src={responseImgURL}
              alt={title}
              className="h-auto md:h-[450px] w-auto max-w-full object-contain rounded"
            />
          )
        ) : isPdf ? (
          <div className="w-[400px] max-w-full h-[550px] overflow-hidden bg-white p-2 border border-gray-200 rounded">
            {enablePreviewZoom && (
              <div className="flex items-center gap-2 mb-2 no-print">
                <button
                  type="button"
                  className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                  onClick={() =>
                    setPdfScale((prev) =>
                      Math.max(0.5, Number((prev - previewZoomStep).toFixed(2))),
                    )
                  }
                  aria-label="Zoom out"
                >
                  -
                </button>
                <button
                  type="button"
                  className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                  onClick={() =>
                    setPdfScale((prev) => Math.min(4, Number((prev + previewZoomStep).toFixed(2))))
                  }
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  className="px-2 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50"
                  onClick={() => setPdfScale(1)}
                  aria-label="Reset zoom"
                >
                  Reset
                </button>
              </div>
            )}

            <div
              ref={pdfViewportRef}
              className="w-full h-[550px] overflow-auto bg-white border border-gray-100"
              style={{
                cursor:
                  enablePreviewZoom && pdfScale > 1
                    ? isPdfDragging
                      ? "grabbing"
                      : "grab"
                    : "default",
                userSelect: isPdfDragging ? "none" : "auto",
              }}
              onPointerDown={enablePreviewZoom ? handlePdfPointerDown : undefined}
              onPointerMove={enablePreviewZoom ? handlePdfPointerMove : undefined}
              onPointerUp={enablePreviewZoom ? stopPdfDragging : undefined}
              onPointerLeave={enablePreviewZoom ? stopPdfDragging : undefined}
              onPointerCancel={enablePreviewZoom ? stopPdfDragging : undefined}
            >
              {pdfLoadError ? (
                <div className="p-2 text-red-600 text-xs">{pdfLoadError}</div>
              ) : (
                <Document
                  file={responseImgURL}
                  onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                  onLoadError={() =>
                    setPdfLoadError(t("security-clearance.preview-unsupported-type"))
                  }
                  loading={<div className="p-2 text-xs text-gray-600">Loading PDF...</div>}
                >
                  {Array.from({ length: pdfNumPages || 1 }, (_, index) => (
                    <div key={`${title}-page-${index + 1}`} className="mb-2 w-max min-w-full">
                      <Page
                        pageNumber={index + 1}
                        width={Math.max(220, Math.floor((pdfViewportWidth || 360) - 12))}
                        scale={enablePreviewZoom ? pdfScale : 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                  ))}
                </Document>
              )}
            </div>
          </div>
        ) : isDocx ? (
          <div
            ref={docxRef}
            className="w-full h-[450px] overflow-auto text-[10px] leading-snug p-2 bg-white"
            aria-label={title}
          />
        ) : isExcel ? (
          <div
            ref={excelRef}
            className="w-full h-[450px] overflow-auto text-[10px] leading-snug p-2 bg-white"
            aria-label={title}
          />
        ) : null}
      </div>
    </div>
  );
};

export default FilePreview;
